import { z } from 'zod'

/* Spec PARTNER-001 — contrato da API /api/v1/partner. Servidor e telas usam os mesmos schemas e tipos. */

export const API_BASE = '/api/v1/partner'

export const ROLES = ['manager', 'staff'] as const
export type Role = (typeof ROLES)[number]

export const GENDERS = ['female', 'male', 'other'] as const
export type Gender = (typeof GENDERS)[number]

/** Situação da criança (spec, "Situação da criança"). As 4 primeiras são do cadastro feito no app. */
export const CHILD_STATUSES = ['draft', 'in_review', 'returned', 'approved', 'available', 'sponsored', 'inactive'] as const
export type ChildStatus = (typeof CHILD_STATUSES)[number]
export const CHILD_EDITABLE: readonly ChildStatus[] = ['draft', 'returned']


const trimmed = (min: number, max: number) => z.string().trim().min(min).max(max)

export const emailSchema = z.string().trim().toLowerCase().email().max(254)
/** 10 a 128 caracteres, com letra e número. */
export const passwordSchema = z.string().min(10).max(128).regex(/[A-Za-z]/).regex(/\d/)
export const orgCodeSchema = z.string().trim().regex(/^\d{3,8}$/)
export const challengeIdSchema = z.string().uuid()
export const codeSchema = z.string().regex(/^\d{6}$/)

/** Idiomas das mensagens e da interface (os 5 do app do padrinho; PARTNER-005 C#1: uma escolha so para os dois). */
export const MESSAGE_LOCALES = ['pt-BR', 'en-US', 'es-ES', 'de-DE', 'ja-JP'] as const
export type MessageLocale = (typeof MESSAGE_LOCALES)[number]
/** PARTNER-005 D-06: idioma da tela nos pedidos sem login (o e-mail com o codigo sai nele). */
const screenLocaleSchema = z.enum(MESSAGE_LOCALES).optional()

export const signupSchema = z.object({
    orgCode: orgCodeSchema,
    name: trimmed(3, 120),
    email: emailSchema,
    password: passwordSchema,
    acceptTerms: z.literal(true),
    locale: screenLocaleSchema,
})
export type SignupInput = z.infer<typeof signupSchema>

export const verifySchema = z.object({ challengeId: challengeIdSchema, code: codeSchema })
export const resendSchema = z.object({ challengeId: challengeIdSchema, locale: screenLocaleSchema })
export const loginSchema = z.object({ email: emailSchema, password: z.string().min(1).max(128), remember: z.boolean().default(false) })
export type LoginInput = z.infer<typeof loginSchema>
export const forgotSchema = z.object({ email: emailSchema })
export const resetSchema = z.object({ challengeId: challengeIdSchema, code: codeSchema, password: passwordSchema })

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(v => !Number.isNaN(Date.parse(`${v}T00:00:00Z`)))

// ─── Perfil da crianca para o padrinho (Spec PARTNER-003 D-01) ──────────────
// Listas fechadas (rotulos no i18n). O perfil e um rascunho: tudo opcional ao salvar; profileMissing diz o que falta
// para enviar para analise. Sem nomes de ninguem (FR-007): so escolhas e textos curtos.
export const PERSONALITY_TRAITS = [
    'cheerful', 'calm', 'affectionate', 'communicative', 'creative', 'curious', 'outgoing', 'shy', 'active', 'studious', 'playful', 'observant',
] as const
export const SCHOOL_STAGES = ['not_attending', 'daycare', 'preschool', 'elementary_early', 'elementary_late', 'high_school'] as const
export const SCHOOL_NETWORKS = ['public', 'private', 'not_applicable'] as const
export const LIVES_WITH = [
    'mother', 'father', 'grandmother', 'grandfather', 'stepmother', 'stepfather', 'aunt_uncle', 'other_relatives', 'non_relative_guardian',
] as const
export const PARENTS_STATUS = ['together', 'separated', 'mother_deceased', 'father_deceased', 'both_deceased', 'not_informed'] as const
export const INCOME_COVERS = ['yes', 'partly', 'no'] as const
export const HOUSING_TENURE = ['owned', 'rented', 'lent', 'other'] as const
export const HOUSING_MATERIAL = ['masonry', 'wood', 'wattle_daub', 'mixed', 'other'] as const
export const HOUSE_FEATURES = ['bathroom', 'electricity', 'piped_water', 'treated_water', 'sewage', 'internet'] as const
export const CHILD_NEEDS = [
    'education', 'educational_support', 'health', 'nutrition', 'protection', 'family_community', 'culture', 'sports_leisure',
] as const

export const PROFILE_LIMITS = {
    personality: 3, needs: 4, siblings: 15,
    likes: 200, dream: 120, pet: 80, chores: 160, favoriteSubject: 60, livelihood: 120, about: 1000,
} as const

/** Texto opcional: vazio vira "nao informado". */
const optionalText = (max: number) => z.string().trim().max(max).transform(v => v || undefined).optional()
/** Escolha multipla sem repeticao. */
const choices = <T extends readonly [string, ...string[]]>(values: T, max: number) =>
    z.array(z.enum(values)).max(max).refine(list => new Set(list).size === list.length, { message: 'duplicate' }).optional()

export const childProfileSchema = z.object({
    personality: choices(PERSONALITY_TRAITS, PROFILE_LIMITS.personality),
    likes: optionalText(PROFILE_LIMITS.likes),
    dream: optionalText(PROFILE_LIMITS.dream),
    pet: optionalText(PROFILE_LIMITS.pet),
    chores: optionalText(PROFILE_LIMITS.chores),
    schoolStage: z.enum(SCHOOL_STAGES).optional(),
    schoolNetwork: z.enum(SCHOOL_NETWORKS).optional(),
    favoriteSubject: optionalText(PROFILE_LIMITS.favoriteSubject),
    livesWith: choices(LIVES_WITH, LIVES_WITH.length),
    siblings: z.number().int().min(0).max(PROFILE_LIMITS.siblings).optional(),
    parentsStatus: z.enum(PARENTS_STATUS).optional(),
    livelihood: optionalText(PROFILE_LIMITS.livelihood),
    incomeCovers: z.enum(INCOME_COVERS).optional(),
    housingTenure: z.enum(HOUSING_TENURE).optional(),
    housingMaterial: z.enum(HOUSING_MATERIAL).optional(),
    houseFeatures: choices(HOUSE_FEATURES, HOUSE_FEATURES.length),
    needs: choices(CHILD_NEEDS, PROFILE_LIMITS.needs),
    about: optionalText(PROFILE_LIMITS.about),
}).strict()
export type ChildProfile = z.infer<typeof childProfileSchema>
export type ProfileField = keyof ChildProfile

/** Spec PARTNER-003 "Campos do perfil": o que falta para enviar para analise (mesma regra na tela e no servidor). */
export function profileMissing(profile: ChildProfile | null): ProfileField[] {
    const p = profile ?? {}
    const missing: ProfileField[] = []
    if (!p.personality?.length) missing.push('personality')
    if (!p.likes) missing.push('likes')
    if (!p.schoolStage) missing.push('schoolStage')
    if (p.schoolStage && p.schoolStage !== 'not_attending' && !p.schoolNetwork) missing.push('schoolNetwork')
    if (!p.livesWith?.length) missing.push('livesWith')
    if (p.siblings === undefined) missing.push('siblings')
    if (!p.livelihood) missing.push('livelihood')
    if (!p.incomeCovers) missing.push('incomeCovers')
    if (!p.housingTenure) missing.push('housingTenure')
    if (!p.housingMaterial) missing.push('housingMaterial')
    if (!p.needs?.length) missing.push('needs')
    return missing
}

export const childInputSchema = z.object({
    fullName: trimmed(3, 160),
    /** AAAA-MM-DD; não pode ser no futuro nem de mais de 18 anos atrás (conferido no servidor). */
    birthDate: isoDate,
    gender: z.enum(GENDERS),
    localityId: z.string().uuid(),
    guardianName: trimmed(3, 160),
    imageConsent: z.boolean(),
    /** Spec PARTNER-003: perfil para o padrinho (rascunho; completo so para enviar). */
    profile: childProfileSchema.default({}),
})
export type ChildInput = z.infer<typeof childInputSchema>
/** O que a tela manda (o perfil pode faltar: vira vazio). */
export type ChildInputDraft = z.input<typeof childInputSchema>

// ─── Marcos na vida da crianca (Spec PARTNER-003 US-003, D-07) ──────────────
export const MILESTONE_KINDS = ['school', 'sports', 'arts', 'personal', 'other'] as const
export type MilestoneKind = (typeof MILESTONE_KINDS)[number]
export const MILESTONE_LIMITS = { inReview: 10, title: 80, body: 500 } as const
export const milestoneInputSchema = z.object({
    kind: z.enum(MILESTONE_KINDS),
    /** AAAA-MM; nao pode ser no futuro (conferido no servidor). */
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
    title: trimmed(3, MILESTONE_LIMITS.title),
    body: trimmed(10, MILESTONE_LIMITS.body),
}).strict()
export type MilestoneInput = z.infer<typeof milestoneInputSchema>
export type MilestoneStatus = 'in_review' | 'approved' | 'returned'
export type ChildMilestone = {
    id: string
    kind: MilestoneKind
    month: string
    title: string
    body: string
    status: MilestoneStatus
    reviewNote: string | null
    /** Link temporario (15 min); null sem foto ou sem armazenamento. */
    photoUrl: string | null
    submittedAt: string
    reviewedAt: string | null
    canEdit: boolean
    canDelete: boolean
}

/** Spec PARTNER-003 US-002: foto que o padrinho ve (aprovada) e a que esta em analise ou foi devolvida. */
export type ChildPhoto = {
    current: { url: string | null; approvedAt: string | null } | null
    pending: { url: string | null; status: 'pending' | 'returned'; reviewNote: string | null; uploadedAt: string } | null
}


export type OrganizationRef = { code: string; name: string; city: string | null; state: string | null }

export type Me = {
    user: { id: string; name: string; email: string; role: Role }
    organization: OrganizationRef
    /** PARTNER-005 D-03: idioma salvo da pessoa (interface e mensagens); depois do login vence o do aparelho. */
    locale: MessageLocale
}

export type OrganizationOverview = OrganizationRef & {
    syncedAt: string | null
    localities: { id: string; name: string; children: number }[]
    children: { total: number; sponsored: number; available: number; pending: number }
}

export type LocalityRef = { id: string; name: string }

export type ChildSummary = {
    id: string
    /** Código de 6 dígitos do ChildFund; null nas cadastradas no app. */
    code: string | null
    firstName: string
    ageYears: number | null
    gender: Gender | null
    locality: LocalityRef | null
    status: ChildStatus
    source: 'crm' | 'app'
    /** Spec PWA-012: padrinhos com apadrinhamento ativo (vazio se nao apadrinhada ou cadastrada no app). */
    sponsors: ChildSponsor[]
    /** Spec PARTNER-003: perfil completo para enviar (null nas criancas do Dynamics, que nao tem perfil aqui). */
    profileComplete: boolean | null
    hasPhoto: boolean
    photoInReview: boolean
    milestonesInReview: number
}

export type ChildDetail = ChildSummary & {
    fullName: string
    birthDate: string | null
    guardianName: string | null
    imageConsent: boolean | null
    /** Spec PWA-011: data da autorizacao assinada (AAAA-MM-DD). */
    imageConsentSignedAt: string | null
    reviewNote: string | null
    createdAt: string
    updatedAt: string
    submittedAt: string | null
    canEdit: boolean
    /** Spec PARTNER-003: perfil (null nas criancas do Dynamics) e o que falta para enviar. */
    profile: ChildProfile | null
    profileMissing: ProfileField[]
    /** Desde quando aguarda padrinho (ordem do catalogo). */
    availableSince: string | null
    /** Spec PWA-019: aguardando de novo porque o padrinho do app cancelou (data do cancelamento). */
    sponsorshipEndedAt: string | null
    photo: ChildPhoto
    /** Pode enviar ou trocar a foto (crianca do app, situacao certa; a autorizacao assinada e conferida a parte). */
    canChangePhoto: boolean
    /** Pode registrar marcos (aguardando padrinho ou apadrinhada). */
    canAddMilestone: boolean
    mediaEnabled: boolean
}

// ─── Mensagens dos padrinhos para as criancas (Spec PWA-009) ───────────────
// A OSP entrega a mensagem aprovada pelo ChildFund e digita a resposta da crianca, que tambem passa por analise.
export const MESSAGE_SITUATIONS = ['to_deliver', 'delivered', 'reply_in_review', 'reply_returned', 'replied'] as const
export type MessageSituation = (typeof MESSAGE_SITUATIONS)[number]

export const replyInputSchema = z.object({ body: trimmed(1, 2000) })
export type ReplyInput = z.infer<typeof replyInputSchema>

// Spec PWA-011: fotos e audio anexados a resposta (no maximo 3 fotos + 1 audio de 2 min)
export const ATTACHMENT_LIMITS = { photos: 3, audios: 1, photoBytes: 3 * 1024 * 1024, audioBytes: 5 * 1024 * 1024, audioSeconds: 120, photoMaxSide: 1600 } as const
export type ReplyAttachment = {
    id: string; kind: 'photo' | 'audio'; showsChild: boolean; contentType: string; url: string
    /** Spec PWA-018 D-08: transcricao ja feita neste app (so audio); ausente = ainda nao transcrito. */
    transcript?: Transcript
}

// ─── Botao "Transcrever" (Spec PWA-018) ───────────────────────────────
export type Transcript = { text: string; language: string }
/** Resposta do botao. Indisponivel nao e erro da tela: e o estado daquele audio (o botao volta). */
export type TranscriptionResult =
    | {
        status: 'done'; text: string; language: string
        /** PWA-020: so no audio do padrinho, a traducao para o idioma das mensagens da pessoa (null = sem traducao). */
        translation?: Translation | null
    }
    | { status: 'unavailable'; reason: 'too_long' | 'format' | 'quota' | 'failed' }
/** reply: audio da crianca (resposta ou iniciada pela OSP); sponsor: audio do padrinho. */
export type TranscriptionSource = 'reply' | 'sponsor'

// ─── Traducao automatica (Spec PWA-020 D-11) ──────────────────────────
/* MESSAGE_LOCALES / MessageLocale: no comeco do arquivo (PARTNER-005, tambem nos pedidos sem login). */
export const messageLocaleInputSchema = z.object({ locale: z.enum(MESSAGE_LOCALES) }).strict()
export type MessageLocaleInput = z.infer<typeof messageLocaleInputSchema>
/** "Idioma" da pessoa (interface e mensagens); enabled = traducao das mensagens ligada no servidor. */
export type MessageLocalePreference = { locale: MessageLocale; enabled: boolean }
/** Traducao automatica de um texto do padrinho; `from` = idioma de origem (o do envio ou o que o Google detectou). */
export type Translation = { text: string; from: string }
/** "Traduzir" sob demanda (mensagem ou transcricao do audio do padrinho). */
export type TranslateResult =
    | { status: 'done'; translation: Translation }
    /** Ja esta no idioma escolhido (mensagem antiga, sem idioma, que o Google detectou). */
    | { status: 'same_language' }
    /** Teto do mes, limite, falha ou demora: fica o original (pode tentar de novo). */
    | { status: 'unavailable' }

/** Transcricao do audio do padrinho com a traducao (PWA-020): para o idioma da pessoa e, para a carta impressa, para o
    portugues (C#12). null = sem traducao (mesmo idioma, desligada ou ainda nao pronta). */
export type SponsorTranscript = Transcript & { translation: Translation | null; printTranslation: Translation | null }

// Spec PWA-011: autorizacao de imagem assinada pelo responsavel, registrada pela OSP na ficha da crianca
export const imageConsentInputSchema = z.object({
    consent: z.boolean(),
    signedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
}).refine(v => !v.consent || v.signedAt !== null, { path: ['signedAt'], message: 'required' })
export type ImageConsentInput = z.infer<typeof imageConsentInputSchema>

/** Spec PWA-013: foto ou audio que o padrinho anexou a mensagem (so de mensagens aprovadas; link temporario). */
export type SponsorAttachment = { id: string; kind: 'photo' | 'audio'; url: string; transcript?: SponsorTranscript }

export type ChildMessage = {
    id: string
    child: { id: string; firstName: string; code: string | null; imageConsent: boolean }
    /** So o primeiro nome do padrinho (nunca contato nem sobrenome). */
    sponsorFirstName: string
    /** Spec PWA-012: id do padrinho (CRM) para abrir a conversa com ele; null se ele nao e mais padrinho da crianca
        ou se o app do padrinho ainda nao informa (sem a migracao 006). */
    sponsorId: string | null
    body: string
    /** PWA-020 D-09: idioma em que o padrinho escreveu (o do app dele no envio ou o detectado); null = desconhecido. */
    locale: string | null
    /** PWA-020: `body` traduzido para o idioma das mensagens da pessoa; null = sem traducao (mesmo idioma, desligada,
        teto, falha ou demora: a tela oferece "Traduzir"). */
    translation: Translation | null
    /** PWA-020 C#12: `body` traduzido para o portugues, para a carta impressa (independe do idioma da pessoa); null = ja
        em portugues ou sem traducao. So nas mensagens que ainda podem ser impressas. */
    printTranslation: Translation | null
    /** Spec PWA-013: foto e audio do padrinho, para a OSP mostrar e tocar para a crianca (vazio sem anexos ou sem R2). */
    sponsorAttachments: SponsorAttachment[]
    approvedAt: string
    deliveredAt: string | null
    reply: {
        id: string
        body: string
        status: 'in_review' | 'approved' | 'returned'
        reviewNote: string | null
        submittedAt: string
        attachments: ReplyAttachment[]
    } | null
    situation: MessageSituation
}

// ─── A OSP escreve primeiro ou retoma a conversa (Spec PWA-012) ───────────
export const STARTED_DAILY_LIMIT = 5

/** Padrinho da crianca: so o primeiro nome. `account` = empresa (nao recebe mensagem).
    `onApp`: tem conta ativa no app do padrinho; null = nao deu para saber (app do padrinho fora do ar). */
export type ChildSponsor = { id: string; firstName: string | null; kind: 'contact' | 'account'; onApp: boolean | null }

export type StartedStatus = 'in_review' | 'approved' | 'rejected' | 'canceled'
/** Mensagem que a OSP escreveu primeiro, em nome da crianca. Recusada: so leitura, com motivo e orientacao. */
export type StartedMessage = {
    id: string
    body: string
    status: StartedStatus
    rejectionReason: string | null
    rejectionGuidance: string | null
    submittedAt: string
    reviewedAt: string | null
    attachments: ReplyAttachment[]
}

/** Um item da conversa da crianca com um padrinho, em ordem de data (`at`). */
export type ConversationItem =
    | { type: 'sponsor'; at: string; message: ChildMessage }
    | { type: 'started'; at: string; message: StartedMessage }

export type Conversation = {
    child: ChildMessage['child']
    sponsor: ChildSponsor
    items: ConversationItem[]
    /** Iniciadas hoje (America/Sao_Paulo) para esta crianca, de STARTED_DAILY_LIMIT. */
    startedToday: number
    mediaEnabled: boolean
    /** Spec PWA-018: o botao "Transcrever" aparece nos audios. */
    transcriptionEnabled: boolean
    /** PWA-020: traducao ligada no servidor ("Traduzir" e selo) e o idioma das mensagens da pessoa. */
    translationEnabled: boolean
    messageLocale: MessageLocale
}

/** Mensagens (Spec PWA-009) com as mesmas chaves da conversa. */
export type MessageList = {
    items: ChildMessage[]
    mediaEnabled: boolean
    transcriptionEnabled: boolean
    translationEnabled: boolean
    messageLocale: MessageLocale
}

export type ChallengeResponse = { challengeId: string; destinationMasked: string }

// ─── Avisos da OSP (Spec PARTNER-002, copia do admin-app ADMIN-002) ───

/** D-01: so servicos de push conhecidos (o servidor nao chama enderecos quaisquer). */
const PUSH_HOSTS = [/^fcm\.googleapis\.com$/, /\.push\.services\.mozilla\.com$/, /\.push\.apple\.com$/, /\.notify\.windows\.com$/]

export function isPushEndpoint(value: string): boolean {
    let url: URL
    try {
        url = new URL(value)
    } catch {
        return false
    }
    return url.protocol === 'https:' && url.port === '' && !url.username && !url.password && PUSH_HOSTS.some(host => host.test(url.hostname))
}

export const pushEndpointSchema = z.string().max(2048).refine(isPushEndpoint)
/** Como o navegador entrega (PushSubscription.toJSON): p256dh = ponto P-256 de 65 bytes (comeca com 0x04 → "B"),
    auth = 16 bytes, os dois em base64url sem preenchimento. */
export const pushSubscriptionSchema = z.object({
    endpoint: pushEndpointSchema,
    keys: z.object({
        p256dh: z.string().regex(/^B[A-Za-z0-9_-]{86}$/),
        auth: z.string().regex(/^[A-Za-z0-9_-]{21}[AQgw]$/),
    }),
})
export type PushSubscriptionInput = z.infer<typeof pushSubscriptionSchema>
export const pushEndpointBodySchema = z.object({ endpoint: pushEndpointSchema })
export type PushConfig = { enabled: boolean; publicKey: string | null }

/** Spec PARTNER-004 D-03 — avisos que a pessoa quer receber (por grupo; valem em todos os aparelhos dela). */
export const NOTIFICATION_GROUPS = ['inbox', 'replyReturned', 'startedRejected', 'reviews'] as const
export type NotificationGroup = (typeof NOTIFICATION_GROUPS)[number]
export type NotificationPreferences = Record<NotificationGroup, boolean>
export const notificationPreferencesInputSchema = z.object({
    inbox: z.boolean(),
    replyReturned: z.boolean(),
    startedRejected: z.boolean(),
    reviews: z.boolean(),
}).partial().strict().refine(v => Object.keys(v).length > 0)
export type NotificationPreferencesInput = z.infer<typeof notificationPreferencesInputSchema>

/** Spec PARTNER-004 D-04 — trocar a senha com a sessao aberta (nova senha com as regras do cadastro). */
export const changePasswordSchema = z.object({ currentPassword: z.string().min(1).max(128), newPassword: passwordSchema }).strict()
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>

export const ERROR_CODES = [
    'PARTNER_VALIDATION',
    'PARTNER_ORG_NOT_FOUND',
    'PARTNER_CODE_DELIVERY_UNAVAILABLE',
    'PARTNER_RATE_LIMITED',
    'PARTNER_CODE_INVALID',
    'PARTNER_CODE_EXPIRED',
    'PARTNER_INVALID_CREDENTIALS',
    'PARTNER_ACCOUNT_LOCKED',
    'PARTNER_ACCOUNT_PENDING',
    'PARTNER_ACCOUNT_REJECTED',
    'PARTNER_ACCOUNT_DISABLED',
    'PARTNER_EMAIL_NOT_VERIFIED',
    'PARTNER_SESSION_REQUIRED',
    'PARTNER_NOT_FOUND',
    'PARTNER_NOT_EDITABLE',
    'PARTNER_CRM_UNAVAILABLE',
    'PARTNER_UNAVAILABLE',
    // Spec PWA-011
    'PARTNER_MEDIA_UNAVAILABLE',
    'PARTNER_CONSENT_REQUIRED',
    'PARTNER_ATTACHMENT_LIMIT',
    'PARTNER_ATTACHMENT_INVALID',
    // Spec PWA-012
    'PARTNER_THROTTLED',
    'PARTNER_SPONSOR_IS_COMPANY',
    // Spec PARTNER-002
    'PARTNER_PUSH_UNAVAILABLE',
    'PARTNER_PUSH_NOT_FOUND',
    'PARTNER_PUSH_GONE',
    // Spec PWA-018: transcricao desligada no servidor (sem a chave ou sem armazenamento)
    'PARTNER_TRANSCRIPTION_UNAVAILABLE',
    // Spec PWA-020: traducao desligada no servidor (sem a chave)
    'PARTNER_TRANSLATION_UNAVAILABLE',
    // Spec PARTNER-003
    'PARTNER_PROFILE_INCOMPLETE',
    'PARTNER_MILESTONE_LIMIT',
    // Spec PARTNER-004: senha atual errada ao trocar a senha
    'PARTNER_PASSWORD_WRONG',
] as const
export type ErrorCode = (typeof ERROR_CODES)[number]
